import type { MessageKind } from '../types/messageState';

interface MessageRegistryEntry {
  kind: MessageKind;
  currentVersion: number;
}

export const MESSAGE_REGISTRY = {
  // The first-run "Welcome to Ora!" card, shown session-wide (`SessionGates`).
  // It was step one of the three part-1 tours below; finishing any of those
  // counts as having seen it.
  'welcome-to-ora': { kind: 'tour', currentVersion: 1 },
  'nbme-tour-part-1': { kind: 'tour', currentVersion: 1 },
  'nbme-tour-part-2': { kind: 'tour', currentVersion: 1 },
  'nbme-tour-part-3': { kind: 'tour', currentVersion: 1 },
  'preclinical-tour-part-1': { kind: 'tour', currentVersion: 1 },
  'preclinical-empty-tour-part-1': { kind: 'tour', currentVersion: 1 },
  'flashcards-tour': { kind: 'tour', currentVersion: 1 },
  // A single "Got It" popup, not a multi-step tour — classified as a tip. Key
  // keeps the legacy `-tour` suffix so dual-read/write against onboardingTips works.
  'linked-flashcard-tour': { kind: 'tip', currentVersion: 1 },
  'qbank-tour': { kind: 'tour', currentVersion: 1 },
  'app-promo': { kind: 'tip', currentVersion: 1 },
  // One-time reminder shown the first time a user attaches an image in chat,
  // warning not to upload PHI / sensitive information.
  'chat-image-phi-reminder': { kind: 'tip', currentVersion: 1 },
  'promote-referral': { kind: 'tip', currentVersion: 2 },
  'highlight-refer-friend': { kind: 'tip', currentVersion: 1 },
  'tutor-mode-callout-feedback': { kind: 'tip', currentVersion: 1 },
  // Anki-sync dismissal (SchoolMain). Legacy fallback is the
  // `hasDismissedAnkiSync` boolean column, not an onboardingTips key.
  'anki-sync-dismissed': { kind: 'tip', currentVersion: 1 },
  // Recurring experience/content feedback prompts. Cooldown-gated via
  // lastShownAt; legacy fallback is the `lastSessionFeedbackDate` JSON.
  'session-feedback:overall': { kind: 'prompt', currentVersion: 1 },
  'session-feedback:qbank': { kind: 'prompt', currentVersion: 1 },
  'session-feedback:videos': { kind: 'prompt', currentVersion: 1 },
  'session-feedback:flashcards': { kind: 'prompt', currentVersion: 1 },
} as const satisfies Record<string, MessageRegistryEntry>;

// Dynamic namespaces: keys of the form `<namespace>:<instance-id>` — one row per
// unique instance. Mirror of the BE allowlist. Used for multi-instance
// dismissals where a NEW instance (promo/contract) must re-show even after a
// different one was dismissed.
export const DYNAMIC_MESSAGE_NAMESPACES = {
  'discount-dismissed': { kind: 'tip', currentVersion: 1 },
  'institutional-expired': { kind: 'tip', currentVersion: 1 },
} as const satisfies Record<string, MessageRegistryEntry>;

type StaticMessageKey = keyof typeof MESSAGE_REGISTRY;
type DynamicNamespace = keyof typeof DYNAMIC_MESSAGE_NAMESPACES;
// Instance ids may contain colons (e.g. an ISO endedAt); only the first colon
// delimits the namespace.
export type DynamicMessageKey = `${DynamicNamespace}:${string}`;
export type MessageKey = StaticMessageKey | DynamicMessageKey;

const messageKeyNamespace = (key: string): string | null => {
  const index = key.indexOf(':');
  return index > 0 ? key.slice(0, index) : null;
};

export const getRegistryEntry = (key: MessageKey): MessageRegistryEntry => {
  if (key in MESSAGE_REGISTRY) {
    return MESSAGE_REGISTRY[key as StaticMessageKey];
  }
  const namespace = messageKeyNamespace(key);
  if (
    namespace &&
    // Require a non-empty instance id after the colon (mirrors the BE, which
    // rejects `discount-dismissed:`).
    key.length > namespace.length + 1 &&
    namespace in DYNAMIC_MESSAGE_NAMESPACES
  ) {
    return DYNAMIC_MESSAGE_NAMESPACES[namespace as DynamicNamespace];
  }
  // Unreachable for well-typed keys (MessageKey only permits known static keys
  // or the registered dynamic namespaces); defensive fallback.
  throw new Error(`Unknown message key: ${key}`);
};
